/**
 * A channel's workflows menu (Slack's channel header bolt): its published link workflows, each with Run, plus
 * "New workflow" and "Manage workflows" (the builder). Running one with a form opens the form (AppModalViewModel).
 */
import type { WorkflowShortcut } from "@bundle/core";
import { useState } from "react";
import { Button, Icon, IconButton, Popover, ScrollView, Stack, Text } from "../../primitives/index.ts";
import { useT } from "../../i18n/I18nProvider.tsx";
import { openWorkflows } from "./WorkflowsHost.tsx";

export type ChannelWorkflowsButtonProps = {
  channelId: string;
  workflows: WorkflowShortcut[] | undefined;
  /** The workflow being started. */
  running?: string;
  onOpen: () => void;
  onRun: (workflowId: string) => void;
  testID: string;
};

export function ChannelWorkflowsButton(p: ChannelWorkflowsButtonProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const list = p.workflows ?? [];
  const toggle = () => {
    if (!open) p.onOpen();
    setOpen(!open);
  };
  return (
    <Popover open={open} onClose={() => setOpen(false)} side="bottom" align="end" width="picker" anchor={<IconButton icon="sparkles" accessibilityLabel={t("workflow.menu.title")} onPress={toggle} testID={p.testID} />}>
      <Stack gap="2" padding="3" testID={`${p.testID}-menu`}>
        <Text weight="semibold">{t("workflow.menu.title")}</Text>
        {list.length === 0 ? (
          <Text variant="footnote" color="text.secondary" testID={`${p.testID}-empty`}>
            {t("workflow.menu.empty")}
          </Text>
        ) : (
          <ScrollView>
            <Stack gap="1">
              {list.map((w) => (
                <Stack key={w.id} direction="horizontal" align="center" gap="2" padding="1" testID={`${p.testID}-item-${w.id}`}>
                  {w.icon === undefined ? <Icon name="sparkles" size="icon.sm" color="text.secondary" /> : <Text>{w.icon}</Text>}
                  <Stack grow gap="0.5">
                    <Text weight="medium" lines={1}>
                      {w.name}
                    </Text>
                    {w.description === undefined ? null : (
                      <Text variant="caption" color="text.secondary" lines={1}>
                        {w.description}
                      </Text>
                    )}
                  </Stack>
                  <Button
                    label={t("workflow.menu.run")}
                    size="sm"
                    variant="secondary"
                    loading={p.running === w.id}
                    onPress={() => {
                      setOpen(false);
                      p.onRun(w.id);
                    }}
                    testID={`${p.testID}-run-${w.id}`}
                  />
                </Stack>
              ))}
            </Stack>
          </ScrollView>
        )}
        <Stack direction="horizontal" gap="2" justify="end">
          <Button
            label={t("workflow.menu.manage")}
            variant="ghost"
            size="sm"
            onPress={() => {
              setOpen(false);
              openWorkflows();
            }}
            testID={`${p.testID}-manage`}
          />
          <Button
            label={t("workflow.list.new")}
            icon="plus"
            variant="secondary"
            size="sm"
            onPress={() => {
              setOpen(false);
              openWorkflows({ newInChannel: p.channelId });
            }}
            testID={`${p.testID}-new`}
          />
        </Stack>
      </Stack>
    </Popover>
  );
}
